import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import axios from 'src/utils/axios';
import type { KBArticleSummary } from './types';

interface CitationCardProps {
  articleId: number;
}

/** Strip markdown emphasis and leading outline markers like "(a)", "(i)" from titles. */
function cleanTitle(title: string): string {
  return title
    .replace(/^\s*\([a-zA-Z0-9]+\)\s*/, '')
    .replace(/\*+/g, '')
    .replace(/_+/g, '')
    .trim();
}

/**
 * One source, one row (PA-05).
 *
 * The old card was three lines — icon, wrapped title, then a repeated "Open article"
 * link — so four sources filled the panel and pushed the answer out of view, and a
 * fifth sat behind the composer. Now the whole row *is* the link: icon, single-line
 * title with an ellipsis, nothing else. Half the height, one click target, no repeated
 * call to action.
 *
 * The row keeps a real `<a href>` rather than an `onClick` so it stays keyboard
 * reachable and opens in a new tab on middle-click, the way a link is expected to.
 */
const CitationCard: FC<CitationCardProps> = ({ articleId }) => {
  const [article, setArticle] = useState<KBArticleSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchArticle = async (): Promise<void> => {
      try {
        const { data } = await axios.get<KBArticleSummary[]>(
          `/support-hub/articles/?ids=${articleId}`
        );
        const match = Array.isArray(data)
          ? data.find((a) => a.id === articleId) || data[0]
          : data;
        if (!cancelled) {
          setArticle(match || null);
        }
      } catch {
        if (!cancelled) {
          setArticle(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchArticle();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1,
    py: 0.5,
    minHeight: 32,
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
  } as const;

  if (loading) {
    return (
      <Box sx={rowSx}>
        <Skeleton variant="circular" width={16} height={16} />
        <Skeleton width="60%" height={16} />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={rowSx} data-testid="citation-unavailable">
        <ArticleOutlinedIcon
          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
        />
        <Typography variant="caption" color="text.secondary" noWrap>
          This article is no longer available
        </Typography>
      </Box>
    );
  }

  const href = `/workbench/support/hub/articles/${article.slug}`;

  return (
    <Box sx={{ ...rowSx, p: 0 }} data-testid="citation-row">
      <Link
        href={href}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          minHeight: 32,
          width: '100%',
          minWidth: 0,
          color: 'text.primary',
          '&:hover .citation-title': { textDecoration: 'underline' },
        }}
      >
        <ArticleOutlinedIcon
          sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
        />
        <Typography
          className="citation-title"
          variant="caption"
          noWrap
          sx={{ minWidth: 0, flex: 1, fontWeight: 600 }}
        >
          {cleanTitle(article.title)}
        </Typography>
      </Link>
    </Box>
  );
};

export default CitationCard;
